import isNil from 'lodash/isNil'
import pickBy from 'lodash/pickBy'
import queryString from 'query-string'

import { DEFAULT_SITTER_SEARCH_SORT } from '@app/constants/Misc'
import { MOMENT_QUERY_FORMAT } from '@app/constants/Moment'
import type { SearchParams, SearchQuery } from '@app/constants/StoreTypes'

import { AddressSerializer } from '@app/utils/addressSerializer'
import { defineAll } from '@app/utils/defineAll'
import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'
import { maybeTypeU, valita } from '@app/utils/valita'

import { RT_SitterSearchSort } from '@app/packages/api/v2/types/params'
import type { SitterSearchQuery } from '@app/packages/api/v2/types/params'
import { createAddress } from '@app/packages/geo/Address'
import { MAP_AREA_KIND } from '@app/packages/geo/constants'
import { isValidLatitude } from '@app/packages/geo/Point'
import { resolveThunkSelector, unwrapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import type { StoreDispatch } from '@app/store/dispatch'
import { defaultSearchAddressResultSelector } from '@app/store/selectors/search'
import { sitterSearchConfigResultSelector } from '@app/store/slices/sitter_search_configs'
import { createThunk } from '@app/store/thunk'

import { MIN_MINUTES_TO_START, SITTER_SEARCH_ONLINE_QUERY_KEYS } from './constants'
import { RangeToCurrencyConverter } from './RangeToCurrencyConverter'

export class SearchParamsManager {
  private dispatch: StoreDispatch
  private now: Moment

  additionalParams: Record<string, string | (string | null)[] | null> = {}
  readonly params: SearchParams
  readonly effectiveParams: SearchParams

  constructor(params: SearchParams, dispatch: StoreDispatch, now: Moment) {
    this.dispatch = dispatch
    this.now = now

    this.params = params
    this.effectiveParams = this.getEffectiveParams()
  }

  getActiveFiltersCount() {
    const params = this.params
    return (
      [!!params.special_needs, !!params.kidsout_school, !!params.babies, !!params.min_rate || !!params.max_rate, !!params.kids, !!params.online].filter(
        x => !!x
      ).length + (params.perks?.length || 0)
    )
  }

  withNewParams(cb: (params: SearchParams) => SearchParams) {
    const nm = new SearchParamsManager(cb(this.params), this.dispatch, this.now)
    nm.additionalParams = this.additionalParams
    return nm
  }

  /** Converts params to query that is used in api call */
  async toApiQuery({ page, per_page }: { page?: number; per_page?: number }) {
    const params = this.effectiveParams
    const rangeToCurrencyConverter = await this.dispatch(getRangeToCurrencyConverter())

    let query = defineAll<SitterSearchQuery>({
      online: params.online,
      date: params.only_vacant ? params.date : undefined,
      end: params.only_vacant ? params.end : undefined,
      start: params.only_vacant ? params.start : undefined,
      special_needs: params.special_needs,
      kidsout_school: params.kidsout_school,
      babies: params.babies,
      kids: params.kids,
      perks: params.perks?.length ? params.perks : undefined,
      min_rate: params.min_rate ? Math.round(rangeToCurrencyConverter.toCurrencyValue(params.min_rate)) : undefined,
      max_rate: params.max_rate ? Math.round(rangeToCurrencyConverter.toCurrencyValue(params.max_rate)) : undefined,
      rate_currency: (params.min_rate ?? params.max_rate) ? rangeToCurrencyConverter.currency : undefined,

      /** example: 42.2123123 */
      latitude: params.address?.location.lat,
      /** example: 42.2123123 */
      longitude: params.address?.location.lon,
      distance: undefined,

      'bbox[bl][lat]': params.address?.kind === MAP_AREA_KIND ? params.address.bounds?.bottomLeft.lat : undefined,
      'bbox[bl][lon]': params.address?.kind === MAP_AREA_KIND ? params.address.bounds?.bottomLeft.lon : undefined,
      'bbox[tr][lat]': params.address?.kind === MAP_AREA_KIND ? params.address.bounds?.topRight.lat : undefined,
      'bbox[tr][lon]': params.address?.kind === MAP_AREA_KIND ? params.address.bounds?.topRight.lon : undefined,

      page,
      per_page,

      sort: params.sort,
    })

    if (params.online) {
      query = pickBy(query, (_val, key) => SITTER_SEARCH_ONLINE_QUERY_KEYS.includes(key as any))
    }

    return query
  }

  toQueryString(): string {
    const str = new QueryStringStringifier()
    return str.stringify({ ...this.toQuery(), ...this.additionalParams })
  }

  private getEffectiveParams() {
    const now = this.now
    const params = this.params

    const date = (() => {
      if (!params.only_vacant) return undefined
      if (params.online) return undefined
      if (!params.date) return now.clone().add(1, 'day').format(MOMENT_QUERY_FORMAT)
      if (!moment(params.date, 'YYYYMMDD').isValid()) return now.clone().add(1, 'day').format(MOMENT_QUERY_FORMAT)
      if (moment(params.date, 'YYYYMMDD').isBefore(now, 'day')) return now.clone().add(1, 'day').format(MOMENT_QUERY_FORMAT)
      return params.date
    })()

    const start = (() => {
      if (!params.only_vacant) return undefined
      if (params.online) return undefined
      if (!params.start) return undefined
      if (!isTime(params.start, 30)) return undefined
      if (!params.end || !isTime(params.end, 30)) return undefined
      if (moment(`${params.date} ${params.start}`, 'YYYYMMDD HH:mm').isBefore(moment().add(MIN_MINUTES_TO_START, 'minutes'))) return undefined
      return params.start
    })()

    const end = (() => {
      if (!params.only_vacant) return undefined
      if (params.online) return undefined
      if (!params.end) return undefined
      if (!isTime(params.end, 30)) return undefined
      if (!params.start) return undefined
      return params.end
    })()

    const address = (() => {
      if (params.online) return undefined
      if (!params.address) return this.dispatch(unwrapThunkSelector(defaultSearchAddressResultSelector, 'Default search address is not loaded yet'))
      return params.address
    })()

    return defineAll<SearchParams>({
      only_vacant: params.only_vacant,
      online: params.online,
      date,
      end,
      start,
      address,
      special_needs: params.special_needs,
      kidsout_school: params.kidsout_school,
      babies: params.babies,
      kids: params.kids,
      perks: params.perks?.length ? params.perks : undefined,
      min_rate: params.min_rate,
      max_rate: params.max_rate,
      sort: params.sort ?? DEFAULT_SITTER_SEARCH_SORT,
    })
  }

  /** Converts params to query that is used in website url */
  private toQuery(): SearchQuery {
    const params = this.effectiveParams
    const q: { [key: string]: string | string[] } = {}

    const isOnline = params.online

    for (const key of Object.keys(params) as (keyof SearchParams)[]) {
      const value = params[key]

      switch (true) {
        case isOnline && SITTER_SEARCH_ONLINE_QUERY_KEYS.indexOf(key) === -1:
        case key === 'only_vacant':
          continue
        case key === 'address': {
          const value = params[key]
          if (value) q[key] = AddressSerializer.serialize(value)
          continue
        }
        case key === 'perks': {
          const value = params[key]
          if (value?.length) q.perks = value
          continue
        }
        case typeof value === 'string': {
          if (value) q[key] = value
          continue
        }
        case typeof value === 'number': {
          if (value) q[key] = String(value)
          continue
        }
        case typeof value === 'boolean': {
          if (value) q[key] = '✓'
          continue
        }
        case true:
        default:
          break
      }
    }

    if (q.sort && q.sort === DEFAULT_SITTER_SEARCH_SORT) {
      delete q.sort
    }

    q.busy = params.only_vacant ? 'omit' : 'include'

    return q
  }

  static async create(params: SearchParams, dispatch: StoreDispatch, now: Moment) {
    await dispatch(resolveThunkSelector(defaultSearchAddressResultSelector))
    return new SearchParamsManager(params, dispatch, now)
  }

  static async fromSearchString(searchString: string | undefined, dispatch: StoreDispatch, now: Moment) {
    const q = queryString.parse(searchString || '', { parseNumbers: false, arrayFormat: 'bracket' })
    const additional = pickBy(q, (_v, k) => k.startsWith('utm_'))
    const params = parseQuery(q)
    const m = await SearchParamsManager.create(params, dispatch, now)
    m.additionalParams = additional
    return m
  }

  static getAdditionalParams(searchString: string) {
    const q = queryString.parse(searchString || '', { parseNumbers: false, arrayFormat: 'bracket' })
    return pickBy(q, (_v, k) => k.startsWith('utm_'))
  }

  static searchHasParams(searchString: string | undefined) {
    const q = queryString.parse(searchString || '', { parseNumbers: false, arrayFormat: 'bracket' })
    return Object.keys(pickBy(q, (_v, k) => !k.startsWith('utm_'))).length > 0
  }
}

const TIME_REGEX = /^\d{1,2}:\d{2}$/

/** Check if value is time in form of "HH:mm" i.e 7:00 07:00 12:30 */
const isTime = (value: string, granularity = 1): boolean => {
  if (!TIME_REGEX.test(value)) return false
  const parts = value.split(':').map(v => parseInt(v, 10))
  if (parts[0] >= 24) return false
  if (parts[1] >= 60) return false
  if (parts[1] % granularity !== 0) return false
  return true
}

function parseQuery(query: { [key: string]: string | (string | null)[] | null | undefined }): SearchParams {
  const minRate = maybeTypeU(
    valita.string().map(v => parseFloat(v) || undefined),
    query.min_rate
  )

  const maxRate = maybeTypeU(
    valita.string().map(v => parseFloat(v) || undefined),
    query.max_rate
  )

  const busy = query.busy || 'include'

  const start = maybeTypeU(valita.string(), query.start)

  const params = defineAll<SearchParams>({
    only_vacant: busy === 'omit' ? true : undefined,
    online: parseQueryBoolean(query.online),
    date: maybeTypeU(RT_Date, query.date),
    start,
    end: start && maybeTypeU(valita.string(), query.end),
    address:
      maybeTypeU(
        valita.string().map(v => AddressSerializer.deserialize(v) ?? undefined),
        query.address
      ) ?? parseLegacyAddress(query),
    special_needs: parseQueryBoolean(query.special_needs),
    kidsout_school: parseQueryBoolean(query.kidsout_school),
    babies: parseQueryBoolean(query.babies),
    kids: maybeTypeU(valita.string(), query.kids),
    perks: maybeTypeU(
      valita.array(valita.string()).map(v =>
        v
          .map(v => parseInt(v, 10))
          .filter(v => !isNaN(v))
          .map(v => `${v}`)
      ),
      query.perks
    ),
    min_rate: minRate,
    max_rate: !isNil(minRate) && !isNil(maxRate) && maxRate < minRate ? undefined : maxRate,
    sort: maybeTypeU(RT_SitterSearchSort, query.sort),
  })

  return params
}

export class QueryStringStringifier {
  private querySortKeys: (keyof SearchParams)[] = ['online', 'date', 'start', 'end', 'address']

  stringify(query: { [key: string]: string | number | (string | null)[] | null | undefined }) {
    const q = queryString.stringify(query, { sort: this.querySort, arrayFormat: 'bracket' })
    return q ? `?${q}` : ''
  }

  private querySort = (a: string, b: string) => {
    const pa = this.getPriorityForKey(a)
    const pb = this.getPriorityForKey(b)
    if (pa === pb) return a.localeCompare(b)
    return pa > pb ? -1 : 1
  }

  private getPriorityForKey(key: string) {
    const index = (this.querySortKeys as string[]).indexOf(key)
    if (key.startsWith('utm_')) return 0
    if (index === -1) return 1
    return this.querySortKeys.length - index + 1
  }
}

const RT_Date = valita.string().assert(v => /\d{4}-?\d{2}-?\d{2}/.test(v), "Date doesn't conform to format")

export const parseLegacyAddress = (query: { [key: string]: string | (string | null)[] | null | undefined }) => {
  if (typeof query.address !== 'string') return undefined
  if (typeof query.latitude !== 'string') return undefined
  if (typeof query.longitude !== 'string') return undefined
  const latitude = parseFloat(query.latitude)
  const longitude = parseFloat(query.longitude)
  if (!isValidLatitude(latitude)) return undefined
  if (isNaN(longitude)) return undefined
  return createAddress({ label: query.address, location: { lat: latitude, lon: longitude } })
}

function parseQueryBoolean(value?: any) {
  if (value === '✓' || value === 'true') return true
  if (value === 'false') return false
  return undefined
}

function getRangeToCurrencyConverter() {
  return createThunk(async dispatch => {
    const searchConfig = await dispatch(resolveThunkSelector(sitterSearchConfigResultSelector))
    return new RangeToCurrencyConverter(searchConfig)
  })
}
